import type { ApiResponse } from "@/types";
import {
  type UseQueryResult,
  queryOptions,
  useQuery,
} from "@tanstack/react-query";
import {
  type Snapshot,
  type SnapshotFile,
  SnapshotFileSchema,
  type SnapshotSummary,
} from "../../../schemas";
import { api } from "../api/api";
import { QUERY_KEYS } from "./queryKeys";

export function getProjectSnapshotsQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [QUERY_KEYS.FILESYSTEM, projectId, "snapshots"],
    queryFn: () => api.listProjectSnapshots(projectId),
    select: (
      response: ApiResponse<{
        snapshots: Array<
          Omit<SnapshotSummary, "createdAt"> & { createdAt: string }
        >;
      }>,
    ) => response.data.snapshots,
    enabled: !!projectId,
  });
}

export function getCurrentFileTreeQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [QUERY_KEYS.FILESYSTEM, projectId, "current-files"],
    queryFn: () => api.getCurrentFileTree(projectId),
    select: (response: ApiResponse<{ files: SnapshotFile[] }>) =>
      response.data.files,
    enabled: !!projectId,
  });
}

export function getProjectSnapshotQueryOptions(
  projectId: string,
  snapshotId: string,
) {
  return queryOptions({
    queryKey: [QUERY_KEYS.FILESYSTEM, projectId, "snapshot", snapshotId],
    queryFn: () => api.getProjectSnapshot(projectId, snapshotId),
    select: (
      response: ApiResponse<{
        snapshot: (Omit<Snapshot, "createdAt"> & { createdAt: string }) | null;
      }>,
    ) => response.data.snapshot,
    enabled: !!projectId && !!snapshotId,
  });
}

export function useProjectSnapshots(
  projectId?: string,
): UseQueryResult<
  Array<Omit<SnapshotSummary, "createdAt"> & { createdAt: string }>,
  Error
> {
  return useQuery({
    queryKey: [QUERY_KEYS.FILESYSTEM, projectId, "snapshots"],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }
      return api.listProjectSnapshots(projectId);
    },
    enabled: !!projectId,
    select: (response) => response.data.snapshots,
  });
}

export function useCurrentFileTree(
  projectId?: string,
): UseQueryResult<SnapshotFile[], Error> {
  return useQuery({
    queryKey: [QUERY_KEYS.FILESYSTEM, projectId, "current-files"],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }
      return api.getCurrentFileTree(projectId);
    },
    enabled: !!projectId,
    select: (response) => SnapshotFileSchema.array().parse(response.data.files),
  });
}

export function useProjectSnapshot(
  projectId?: string,
  snapshotId?: string,
): UseQueryResult<
  (Omit<Snapshot, "createdAt"> & { createdAt: string }) | null,
  Error
> {
  return useQuery({
    queryKey: [QUERY_KEYS.FILESYSTEM, projectId, "snapshot", snapshotId],
    queryFn: () => {
      if (!projectId || !snapshotId) {
        throw new Error("Project ID and Snapshot ID are required");
      }
      return api.getProjectSnapshot(projectId, snapshotId);
    },
    enabled: !!projectId && !!snapshotId,
    select: (response) => response.data.snapshot,
  });
}
